import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlay, FaListUl, FaHeart, FaTimes, FaPlus, FaShare } from 'react-icons/fa';
import AddToPartyBtn from "./AddToPartyBtn";
import AddToPlaylistModal from './AddToPlaylistModal';
import ShareModal from './ShareModal';
import { TrackDownloadBtn, AlbumDownloadBtn } from './DownloadButton';
import '../styles/DownloadButton.css';
import '../styles/AlbumCard.css';
import StarRating from './StarRating';
import CommentSection from './CommentSection';
import Recommendations from './Recommendations';

function AlbumCard({ album, playTrack, onFavoriteChange, addToQueue }) {
  const [showModal, setShowModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playlistTrack, setPlaylistTrack] = useState(null);
  const [shareItem, setShareItem] = useState(null);

  useEffect(() => {
    checkFavoriteStatus();
  }, [album.id]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await axios.get(`/api/playlists/favorites/check/${album.id}`);
      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    try {
      if (isFavorite) {
        await axios.delete(`/api/playlists/favorites/${album.id}`);
        setIsFavorite(false);
      } else {
        await axios.post(`/api/playlists/favorites/${album.id}`);
        setIsFavorite(true);
      }
      if (onFavoriteChange) onFavoriteChange();
    } catch (error) {
      console.error('Erreur favori:', error);
    }
  };

  const handleAlbumClick = async () => {
    setShowModal(true);
    setLoading(true);
    try {
      const response = await axios.get(`/api/albums/${album.id}`);
      setTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Erreur chargement pistes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayTrack = (track) => {
    playTrack(track, tracks);
    setShowModal(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCoverUrl = (coverUrl) => {
    if (!coverUrl) return null;
    if (coverUrl.startsWith('http')) return coverUrl;
    const cleanPath = coverUrl.startsWith('/') ? coverUrl : `/${coverUrl}`;
    return `https://disney-music.gpworld.fr${cleanPath}`;
  };

  return (
    <>
      <div className="album-card" onClick={handleAlbumClick}>
        <div className="album-cover">
          {album.cover_url ? (
            <img src={getCoverUrl(album.cover_url)} alt={album.title} />
          ) : (
            <div className="album-cover-placeholder">
              <span>🎵</span>
            </div>
          )}
          <div className="album-overlay">
            <FaPlay className="play-icon" />
          </div>
          <div className="album-actions">
            <button
              className={`action-btn favorite-btn ${isFavorite ? 'active' : ''}`}
              onClick={toggleFavorite}
            >
              <FaHeart />
            </button>
          </div>
        </div>
        <div className="album-info">
          <h3 className="album-title">{album.title}</h3>
          <p className="album-artist">{album.artist}</p>
        </div>
      </div>

      {showModal && (
        <div className="album-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="album-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="album-modal-header">
              <div className="album-modal-info">
                {album.cover_url && (
                  <img 
                    src={getCoverUrl(album.cover_url)}
                    alt={album.title}
                    className="album-modal-cover"
                  />
                )}
                <div>
                  <h2>{album.title}</h2>
                  <p>{album.artist}</p>
                </div>
              </div>
              <div className="album-modal-header-actions">
                <AlbumDownloadBtn album={album} tracks={tracks} />
                <button
                  className="album-header-btn"
                  onClick={() => setShareItem({ type: 'album', albumId: album.id, title: album.title, artist: album.artist })}
                  title="Partager l'album"
                >
                  <FaShare />
                </button>
                <button className="album-modal-close" onClick={() => setShowModal(false)}>
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="album-modal-body">
              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Chargement des pistes...</p>
                </div>
              ) : tracks.length === 0 ? (
                <p className="no-tracks">Aucune piste trouvée</p>
              ) : (
                <div className="tracks-list">
                  {tracks.map((track, index) => {
                    const prevTrack = tracks[index - 1];
                    const showDiscHeader = !prevTrack || prevTrack.disc_number !== track.disc_number;

                    return (
                      <React.Fragment key={track.id}>
                        {showDiscHeader && tracks.some(t => t.disc_number > 1) && (
                          <div className="disc-header">CD {track.disc_number}</div>
                        )}
                        
                        <div className="track-item">
                          <div 
                            className="track-main"
                            onClick={() => handlePlayTrack(track)}
                          >
                            <div className="track-number">{track.track_number}</div>
                            <div className="track-info">
                              <div className="track-title">{track.title}</div>
                              <div className="track-artist">{track.artist}</div>
                            </div>
                            <div className="track-duration">{formatDuration(track.duration)}</div>
                          </div>
                          <div className="track-actions">
                            <TrackDownloadBtn track={track} albumInfo={album} />
                            {addToQueue && <button className="track-action-btn track-queue-btn" onClick={(e) => { e.stopPropagation(); addToQueue(track); }} title="Lire ensuite"><FaListUl /></button>}
                            <button
                              className="track-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPlaylistTrack(track);
                              }}
                              title="Ajouter à une playlist"
                            >
                              <FaPlus />
                            </button>
                            <button
                              className="track-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShareItem({ type: 'track', trackId: track.id, title: track.title, artist: track.artist });
                              }}
                              title="Partager"
                            >
                              <FaShare />
                            </button>
                            <AddToPartyBtn track={track} />
                            <button 
                              className="track-play-btn"
                              onClick={() => handlePlayTrack(track)}
                            >
                              <FaPlay />
                            </button>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Rating, Recommandations & Commentaires */}
            <div className="album-extras">
              <Recommendations albumId={album.id} />
              <div className="extras-collapsible">
                <button className="extras-toggle" onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.parentElement; el.classList.toggle("open"); }}>
                  <span>⭐ Noter &amp; Commenter</span>
                  <span className="extras-arrow">▼</span>
                </button>
                <div className="extras-content">
                  <StarRating type="album" id={album.id} size="md" />
                  <CommentSection type="album" id={album.id} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {playlistTrack && (
        <AddToPlaylistModal 
          track={playlistTrack} 
          onClose={() => setPlaylistTrack(null)} 
        />
      )}

      {shareItem && (
        <ShareModal
          item={shareItem}
          onClose={() => setShareItem(null)}
        />
      )}
    </>
  );
}

export default AlbumCard;
